// The selected watch: subject + lifecycle controls, the busy / failed
// banners, the framed readout, what Merlin is honouring and the run history.
// Refining happens in the chat beside it (kanban 14927fad), so the panel
// carries no refine box; `refineSlot` is where a page can point at that chat.
import { useState, type ReactNode } from 'react';
import {
  CADENCE_OPTIONS,
  lastFailure,
  relativeTime,
  runStatusLabel,
  triggerLabel,
  watchStatusLine,
} from '../../pages/merlinWatchesUtils';
import type { WatchCadence, WatchDetail } from '../../services/merlinWatches';
import { signalsReportsApi } from '../../services/signalsReports';
import styles from './watches.module.css';

export interface WatchPanelProps {
  detail: WatchDetail;
  html: string | null;
  isOwner: boolean;
  busy: boolean;
  showOwner?: boolean;
  onCadence: (cadence: WatchCadence) => void;
  onRunNow: () => void;
  onTogglePause: () => void;
  onRemove: () => Promise<void>;
  /** Rendered under the readout, beside the run history. */
  refineSlot?: ReactNode;
}

export function WatchPanel({
  detail,
  html,
  isOwner,
  busy,
  showOwner = false,
  onCadence,
  onRunNow,
  onTogglePause,
  onRemove,
  refineSlot,
}: WatchPanelProps) {
  const [confirmRemove, setConfirmRemove] = useState(false);
  const w = detail.watch;
  const failure = lastFailure(detail.runs);

  const remove = async () => {
    await onRemove();
    setConfirmRemove(false);
  };

  return (
    <div data-testid="watch-detail" data-target-watch-id={w.id}>
      <div className={styles.detailHead}>
        <div>
          {w.kind === 'briefing' && <span className={styles.kindBadge}>Your daily briefing</span>}
          <h2 className={styles.subject}>{w.subject}</h2>
          <div className={styles.detailMeta}>
            {showOwner && w.owner_name ? `${w.owner_name} · ` : ''}
            {w.run_count} readout{w.run_count === 1 ? '' : 's'} · {watchStatusLine(w)}
          </div>
        </div>
        <div className={styles.actions}>
          <select
            className={styles.select}
            value={w.cadence}
            onChange={(e) => onCadence(e.target.value as WatchCadence)}
            disabled={busy || w.status === 'removed' || !isOwner}
            aria-label="Cadence"
            data-action="watch_cadence_change"
            data-record-value="cadence"
            data-outcome="request"
          >
            {CADENCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={onRunNow}
            disabled={busy || !!w.open_run || w.status === 'removed' || !isOwner}
            title="Ask Merlin to rewrite the readout now (a few minutes)."
            data-action="watch_check_now"
            data-outcome="request"
          >
            {w.open_run ? 'Merlin is checking…' : 'Check now'}
          </button>
          {w.status !== 'removed' && (
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={onTogglePause}
              disabled={busy || !isOwner}
              data-action={w.status === 'paused' ? 'watch_resume' : 'watch_pause'}
              data-outcome="request"
            >
              {w.status === 'paused' ? 'Resume' : 'Pause'}
            </button>
          )}
          {w.status !== 'removed' && !confirmRemove && (
            <button
              type="button"
              className={styles.dangerBtn}
              onClick={() => setConfirmRemove(true)}
              disabled={busy || !isOwner}
              data-action="watch_remove"
              data-outcome="open"
            >
              Remove watch
            </button>
          )}
          {confirmRemove && (
            <span className={styles.confirm}>
              Stop watching this?{' '}
              <button
                type="button"
                className={styles.dangerBtn}
                onClick={() => void remove()}
                disabled={busy}
                data-action="watch_remove_confirm"
                data-outcome="request"
              >
                Yes, remove
              </button>
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={() => setConfirmRemove(false)}
                data-action="watch_remove_cancel"
                data-outcome="none"
              >
                Keep
              </button>
            </span>
          )}
        </div>
      </div>

      {w.open_run && (
        <div className={styles.busyBanner} data-testid="watch-busy">
          Merlin is {w.open_run.status === 'running' ? 'checking now' : 'queued to check'} (
          {triggerLabel(w.open_run.trigger)}). The readout updates here by itself — a few minutes.
        </div>
      )}
      {failure && !w.open_run && (
        <div className={styles.failBanner} data-testid="watch-failed">
          The last check failed ({relativeTime(failure.finished_at ?? failure.queued_at)}):{' '}
          {failure.error || 'no readout was published'}. The schedule is unchanged; press{' '}
          <em>Check now</em> to retry.
        </div>
      )}

      <div className={styles.reportWrap}>
        {w.latest ? (
          <>
            <div className={styles.reportHead}>
              <div>
                <div className={styles.reportTitle}>{w.latest.title}</div>
                <div className={styles.reportMeta}>
                  Readout {w.run_count} · {relativeTime(w.latest.created_at)}
                  {w.latest.summary ? ` · ${w.latest.summary}` : ''}
                </div>
              </div>
              <a
                className={styles.openLink}
                href={signalsReportsApi.viewPath(w.latest.id)}
                target="_blank"
                rel="noopener noreferrer"
                data-action="watch_open_full"
                data-outcome="navigate"
              >
                Open full page ↗
              </a>
            </div>
            {html === null ? (
              <div className={styles.muted}>Loading readout…</div>
            ) : (
              <iframe
                className={styles.frame}
                title="Watch readout"
                srcDoc={html}
                sandbox="allow-popups allow-popups-to-escape-sandbox"
              />
            )}
          </>
        ) : (
          <div className={styles.emptyReport}>
            <div className={styles.emptyTitle}>No readout yet</div>
            {w.open_run
              ? 'Merlin is writing the first one now.'
              : 'Press Check now and Merlin writes the first one.'}
          </div>
        )}
      </div>

      <div className={styles.lower}>
        <section className={styles.honouring} aria-label="What Merlin is honouring">
          <h3>What Merlin is honouring</h3>
          {detail.refinements.length > 0 ? (
            <ol className={styles.refinements}>
              {detail.refinements.map((r, i) => (
                <li key={`${i}-${r}`}>{r}</li>
              ))}
            </ol>
          ) : (
            <p className={styles.hint}>Nothing yet beyond the subject itself.</p>
          )}
          {refineSlot}
        </section>

        <section className={styles.history} aria-label="Run history">
          <h3>Checks</h3>
          <ul className={styles.runList}>
            {detail.runs.map((r) => (
              <li key={r.id} className={styles.runItem}>
                <span className={`${styles.runStatus} ${styles[`run_${r.status}`] ?? ''}`}>
                  {runStatusLabel(r.status)}
                </span>
                <span className={styles.runMeta}>
                  {triggerLabel(r.trigger)} · {relativeTime(r.finished_at ?? r.started_at ?? r.queued_at)}
                  {r.refinement ? ` · “${r.refinement}”` : ''}
                  {r.error ? ` · ${r.error}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}
